import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import {
    BodyMediumRegular,
    BodySmallRegular,
    InfoButtonWithTooltip,
    redactPrivate,
    XStack,
    YStack,
} from 'smoothie';

import {
    getMoneyMapAccountDetailRows,
    getMoneyMapAccountSubtitle,
    type MoneyMapAccountHolder,
} from '../utils/moneyMapAccountDisplay';
import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';
import { MoneyMapAccountRule, type RuleVariant } from './MoneyMapAccountRule';

export type { MoneyMapAccountDetail } from '../utils/moneyMapAccountDisplay';
export type { MoneyMapAccountHolder };
export { getMoneyMapAccountDetailRows, getMoneyMapAccountSubtitle };

interface MoneyMapAccountRuleRedesignProps {
    category: MoneyMapCategory;
    rule: MoneyMapRuleWithAccountDetails;
    moneyMap: MoneyMapWithRulesAndAccounts;
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    showIncomeAccountBadge?: boolean;
    variant?: RuleVariant;
    accountHolders?: MoneyMapAccountHolder[];
}

export const MoneyMapAccountNameWithTooltip = ({
    name,
    account,
    accountHolders,
}: {
    name: string;
    account: MoneyMapAccountWithAccountDetails;
    accountHolders?: MoneyMapAccountHolder[];
}) => {
    const detailRows = getMoneyMapAccountDetailRows(account, accountHolders);

    return (
        <XStack alignItems="center" gap={4}>
            <BodyMediumRegular>{name}</BodyMediumRegular>
            {detailRows.length > 0 && (
                <InfoButtonWithTooltip tooltipContent={detailRows} subtle />
            )}
        </XStack>
    );
};

export const MoneyMapAccountRuleRedesign = ({
    category,
    rule,
    moneyMap,
    accountsById,
    showIncomeAccountBadge,
    variant,
    accountHolders,
}: MoneyMapAccountRuleRedesignProps) => {
    const { moneyMapAccount } = rule;
    const isCashOrInvestment =
        moneyMapAccount.type === MoneyMapAccountType.INTERNAL_BANKING ||
        moneyMapAccount.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT ||
        moneyMapAccount.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

    if (category === MoneyMapCategory.GOALS || !isCashOrInvestment) {
        return (
            <MoneyMapAccountRule
                category={category}
                rule={rule}
                moneyMap={moneyMap}
                accountsById={accountsById}
                showIncomeAccountBadge={showIncomeAccountBadge}
                variant={variant}
            />
        );
    }

    const monthlyAmountCents =
        // BILLS: targetBalanceCents; SPEND: monthlyContributionMaxCents.
        rule.targetBalanceCents ?? rule.monthlyContributionMaxCents ?? 0;

    return (
        <YStack gap={12} padding={16} backgroundColor="$gray100">
            <XStack alignItems="center" gap={12}>
                <MoneyMapAccountIcon
                    category={category}
                    account={moneyMapAccount}
                />
                <YStack flex={1} gap={4}>
                    <MoneyMapAccountNameWithTooltip
                        name={moneyMapAccount.accountDetails.name}
                        account={moneyMapAccount}
                        accountHolders={accountHolders}
                    />
                    <BodySmallRegular color="$gray1000">
                        {getMoneyMapAccountSubtitle(moneyMapAccount)}
                    </BodySmallRegular>
                </YStack>
                {category !== MoneyMapCategory.OTHER_ACCOUNTS && (
                    <BodyMediumRegular {...redactPrivate()}>
                        {`${centsToFormattedDollars(monthlyAmountCents, false)}/mo`}
                    </BodyMediumRegular>
                )}
            </XStack>
        </YStack>
    );
};
